import { ViewportScroller } from '@angular/common';
import { Component, effect, inject, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { NewsletterService } from '../../core/services/newsletter.service';
import { IconComponent, type IconName } from '../../shared/components/icon/icon.component';
import { SnapCarouselComponent } from '../../shared/components/snap-carousel/snap-carousel.component';
import { StarRatingComponent } from '../../shared/components/star-rating/star-rating.component';
import { StripeBadgeComponent } from '../../shared/components/stripe-badge/stripe-badge.component';
import { RevealDirective } from '../../shared/directives/reveal.directive';

interface Testimonial {
  quote: string;
  name: string;
}

interface CategoryTile {
  label: string;
  slug: string;
  image: string;
}

interface TrustBadge {
  title: string;
  subtitle: string;
  icon: IconName;
}

interface FaqEntry {
  question: string;
  answer: string;
}

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    RouterLink,
    SnapCarouselComponent,
    StarRatingComponent,
    IconComponent,
    StripeBadgeComponent,
    RevealDirective,
  ],
  templateUrl: './home.component.html',
})
export class HomeComponent {
  readonly authService = inject(AuthService);
  private readonly newsletterService = inject(NewsletterService);
  private readonly route = inject(ActivatedRoute);
  private readonly viewportScroller = inject(ViewportScroller);

  private readonly fragment = toSignal(this.route.fragment, {
    initialValue: null as string | null,
  });

  readonly trustBadges: TrustBadge[] = [
    {
      title: 'Verified Sellers',
      subtitle: 'ID-checked & rated',
      icon: 'shield',
    },
    {
      title: 'Free to Relist',
      subtitle: 'Not the right fit? Relist at 0%',
      icon: 'arrowPath',
    },
    {
      title: 'Secure Payments',
      subtitle: 'Held until you confirm',
      icon: 'lock',
    },
    {
      title: 'Ships Australia-wide',
      subtitle: 'Tracked, not just local pickup',
      icon: 'truck',
    },
  ];

  readonly categoryTiles: CategoryTile[] = [
    { label: 'Dressage', slug: 'dressage', image: 'assets/categories/dressage.webp' },
    {
      label: 'General Purpose',
      slug: 'general-purpose',
      image: 'assets/categories/general-purpose.webp',
    },
    { label: 'Jump', slug: 'jump-close-contact', image: 'assets/categories/jump.webp' },
    { label: 'Stock', slug: 'australian-stock', image: 'assets/categories/stock.webp' },
    { label: 'Western', slug: 'western', image: 'assets/categories/western.webp' },
    { label: 'Endurance', slug: 'endurance', image: 'assets/categories/endurance.webp' },
    { label: 'Show', slug: 'show', image: 'assets/categories/show.webp' },
    { label: 'Pony', slug: 'pony', image: 'assets/categories/pony.webp' },
  ];

  readonly testimonials: Testimonial[] = [
    {
      quote:
        'Payment held safe until I confirmed everything checked out, and knowing I could resell for free if it didn’t suit gave me the confidence to buy. Never buying tack any other way again.',
      name: 'Amelia R. — Eventer, VIC',
    },
    {
      quote:
        'Sold my old dressage saddle in four days. Verified buyer, secure payment, zero hassle — exactly what tack trading should feel like.',
      name: 'Grace T. — Dressage Rider, NSW',
    },
    {
      quote:
        'Photos matched the saddle exactly. Filtering by tree width saved me hours of scrolling through saddles that would never have fit.',
      name: 'Josh M. — Western Trail, QLD',
    },
  ];

  // A short, hand-picked subset of the full /help FAQ — kept word-for-word
  // in sync with help.component.ts's canonical answers so figures (48-hour
  // inspection window, 8% selling fee, escrow) never drift between the two pages.
  readonly faqs: FaqEntry[] = [
    {
      question: 'How is Galopa different from other marketplaces?',
      answer:
        'Facebook Marketplace is built for face-to-face, local deals — Galopa lets you buy and sell with people anywhere in the country, which completely opens up your market. Every user is identity-verified, and your funds are held securely by Galopa and only released once the saddle is delivered and confirmed to match the listing.',
    },
    {
      question: 'How does Galopa keep the sale low-risk for both buyer and seller?',
      answer:
        'The buyer pays upfront, but funds are captured and held securely by Galopa, not handed to the seller directly — so the seller knows the sale is real before they ship. They’re only released once the saddle is delivered and the buyer confirms it matches the listing, or the 48-hour inspection window passes with no issue reported.',
    },
    {
      question: 'What if the saddle doesn’t fit my horse?',
      answer:
        'The inspection window is for confirming the saddle matches how it was listed, not a general try-it-and-decide period. If it’s exactly as described but simply doesn’t suit your horse, it’s yours to keep — and you can resell it on Galopa any time, for free, at 0% commission.',
    },
    {
      question: 'Are there listing fees?',
      answer:
        'There’s no fee to list. Galopa’s standard selling commission is 8%, deducted from your payout when a sale completes — some sellers qualify for a lower rate.',
    },
    {
      question: 'What is Wanted Saddle?',
      answer:
        'It flips the usual search: instead of browsing listings, you post what saddle you’re after — brand, discipline, size, tree width, budget — and sellers with a matching saddle already listed can respond directly.',
    },
    {
      question: 'How are my funds held?',
      answer:
        'Your card is charged via Stripe once your offer is accepted, and the funds are held securely by Galopa. They’re released once the inspection window resolves — you confirm it’s as described, the 48 hours pass, or your refund is processed.',
    },
    {
      question: 'Why do sellers need identity verification?',
      answer:
        'KYC helps keep the marketplace safe and is processed by Stripe. Accounts that fail verification or engage in fraudulent activity may be suspended.',
    },
    {
      question: 'Can I use Galopa to pay securely for a saddle I found on another marketplace?',
      answer:
        'Yes — that’s <a href="/secure-trade">Secure Trade</a>. Either the buyer or the seller starts a trade for the agreed saddle and price, both need a free Galopa account, and once accepted, Galopa holds the buyer’s payment securely until the saddle is delivered and confirmed to match what was agreed.',
    },
  ];

  readonly openFaqIndex = signal<number | null>(null);

  readonly newsletterEmail = signal('');
  readonly subscribed = signal(false);
  readonly subscribePending = signal(false);
  readonly subscribeError = signal<string | null>(null);

  constructor() {
    effect(() => {
      const id = this.fragment();
      if (!id) return;
      untracked(() => {
        requestAnimationFrame(() => this.viewportScroller.scrollToAnchor(id));
      });
    });
  }

  toggleFaq(index: number): void {
    this.openFaqIndex.update((current) => (current === index ? null : index));
  }

  subscribe(event: Event): void {
    event.preventDefault();
    const email = this.newsletterEmail().trim();
    if (!email || this.subscribePending()) return;

    this.subscribePending.set(true);
    this.subscribeError.set(null);

    this.newsletterService.subscribe(email).subscribe({
      next: () => {
        this.subscribePending.set(false);
        this.subscribed.set(true);
      },
      error: (err: { error?: { message?: string | string[] } }) => {
        this.subscribePending.set(false);
        const message = err.error?.message;
        this.subscribeError.set(
          Array.isArray(message)
            ? message[0]
            : (message ?? 'Something went wrong. Please try again.'),
        );
      },
    });
  }
}
